/**
 * Catalog/PIM transport boundary.
 *
 * Every request/response alias below comes from the generated canonical
 * OpenAPI client. The app's HTTP layer still owns credentials, acting-branch
 * provenance, idempotency retries and ETag headers; this module binds those
 * mechanics to catalog operation types without duplicating wire contracts.
 */
import type { components, operations } from "@erp/api-client";

import {
  API_BASE,
  ACTING_BRANCH_HEADER,
  apiFetchOperational as apiFetch,
  apiRequestOperational as apiRequest,
  type Page,
} from "@/lib/api/http";
import { ApiError } from "@/lib/api/errors";
import { getCurrentBranchId } from "@/lib/branch/branch-store";
import { parseProblemResponse } from "@/lib/api/problem";
import {
  MAX_IDEMPOTENCY_BLOB_BYTES,
  collectCursorPages,
  fetchWithEtag,
  type WithEtag,
} from "@/features/shared/ops-api";

type Schemas = components["schemas"];

export type ProductType = Schemas["CatalogProductType"];
export type ProductStatus = Schemas["CatalogProductStatus"];
export type VariantStatus = Schemas["CatalogVariantStatus"];
export type LifecycleStatus = Schemas["CatalogLifecycleStatus"];
export type BarcodeType = Schemas["CatalogBarcodeType"];
export type AttributeType = Schemas["CatalogAttributeType"];
export type ComponentType = Schemas["CatalogComponentType"];
export type ImportStatus = Schemas["CatalogProductImportStatus"];
export type ImportRowStatus = Schemas["CatalogProductImportRowStatus"];
export type ImportErrorField = Schemas["CatalogProductImportErrorField"];

export const PRODUCT_TYPES = [
  "goods",
  "service",
  "bundle",
] as const satisfies readonly ProductType[];
export const PRODUCT_STATUSES = [
  "draft",
  "active",
  "archived",
] as const satisfies readonly ProductStatus[];
export const VARIANT_STATUSES = [
  "active",
  "inactive",
  "archived",
] as const satisfies readonly VariantStatus[];
export const BARCODE_TYPES = [
  "EAN13",
  "UPC",
  "CODE128",
  "GTIN14",
] as const satisfies readonly BarcodeType[];
export const ATTRIBUTE_TYPES = [
  "text",
  "number",
  "select",
] as const satisfies readonly AttributeType[];
export const IMPORT_STATUSES = [
  "uploaded",
  "validating",
  "validated",
  "committing",
  "committed",
  "failed",
  "discarded",
] as const satisfies readonly ImportStatus[];
export const IMPORT_ROW_STATUSES = [
  "pending",
  "valid",
  "error",
] as const satisfies readonly ImportRowStatus[];
export const IMPORT_ERROR_FIELDS = [
  "file",
  "headers",
  "action",
  "sku",
  "existing_sku",
  "product_name",
  "product_type",
  "base_uom_code",
  "category_code",
  "tax_class_code",
  "product_status",
  "variant_status",
  "attribute_values",
] as const satisfies readonly ImportErrorField[];

export type Uom = Schemas["CatalogUom"];
export type UomConversion = Schemas["CatalogUomConversion"];
export type Attribute = Schemas["CatalogAttribute"];
export type TaxClass = Schemas["CatalogTaxClassLookup"];
export type CategoryPathSegment = Schemas["CatalogCategoryPathSegment"];
export type Category = Schemas["CatalogCategory"];
export type MatchedVariantHint = Schemas["CatalogMatchedVariantHint"];
export type Product = Schemas["CatalogProduct"];
export type Variant = Schemas["CatalogVariant"];
export type VariantBarcode = Schemas["CatalogBarcode"];
export type ProductComponent = Schemas["CatalogProductComponent"];
export type ProductAttribute = Schemas["CatalogProductAttribute"];
export type ProductDetail = Schemas["CatalogProduct"];
export type ProductImport = Schemas["CatalogProductImport"];
export type ImportRowError = Schemas["CatalogProductImportRowError"];
export type ImportAttributeValue = Schemas["CatalogProductImportAttributeValue"];
export type ImportNormalizedRow = Schemas["CatalogProductImportNormalizedRow"];
export type ProductImportRow = Schemas["CatalogProductImportRow"];

export type ProductVariantInput = Schemas["CatalogProductVariantInput"];
export type ProductInput = Schemas["CatalogProductPut"];
export type ProductCreateInput = Schemas["CatalogProductCreate"];
export type BarcodeInput = Schemas["CatalogBarcodeCreate"];
export type ComponentInput = Schemas["CatalogProductComponentCreate"];
export type AttributeInput = Schemas["CatalogAttributePut"];
export type AttributeCreateInput = Schemas["CatalogAttributeCreate"];
export type ProductAttributeInput = Schemas["CatalogProductAttributeCreate"];
export type ProductAttributePatch = Schemas["CatalogProductAttributePatch"];
export type UomConversionInput = Schemas["CatalogUomConversionCreate"];
export type UomCreateInput = Schemas["CatalogUomCreate"];
export type UomPatch = Schemas["CatalogUomPatch"];

export type ProductListQuery = NonNullable<
  operations["catalogListProducts"]["parameters"]["query"]
>;
export type VariantListQuery = NonNullable<
  operations["catalogListVariants"]["parameters"]["query"]
>;
export type ImportListQuery = NonNullable<
  operations["catalogListProductImports"]["parameters"]["query"]
>;
export type ImportRowsQuery = NonNullable<
  operations["catalogListProductImportRows"]["parameters"]["query"]
>;

type ProductPage =
  operations["catalogListProducts"]["responses"][200]["content"]["application/json"];
type VariantPage =
  operations["catalogListVariants"]["responses"][200]["content"]["application/json"];
type AttributePage =
  operations["catalogListAttributes"]["responses"][200]["content"]["application/json"];
type ProductAttributePage =
  operations["catalogListProductAttributes"]["responses"][200]["content"]["application/json"];
type ProductComponentPage =
  operations["catalogListProductComponents"]["responses"][200]["content"]["application/json"];
type BarcodePage =
  operations["catalogListVariantBarcodes"]["responses"][200]["content"]["application/json"];
type UomPage = operations["catalogListUoms"]["responses"][200]["content"]["application/json"];
type UomConversionPage =
  operations["catalogListUomConversions"]["responses"][200]["content"]["application/json"];
type TaxClassPage =
  operations["catalogListTaxClasses"]["responses"][200]["content"]["application/json"];
type CategoryPage =
  operations["catalogListCategories"]["responses"][200]["content"]["application/json"];
type ProductImportPage =
  operations["catalogListProductImports"]["responses"][200]["content"]["application/json"];
type ProductImportRowPage =
  operations["catalogListProductImportRows"]["responses"][200]["content"]["application/json"];

type GeneratedPage<T> = Schemas["PageEnvelope"] & { readonly items: readonly T[] };

function toMutablePage<T>(page: GeneratedPage<T>): Page<T> {
  return { items: [...page.items], next_cursor: page.next_cursor };
}

export async function listProducts(
  query: ProductListQuery,
  signal?: AbortSignal,
): Promise<Page<Product>> {
  return toMutablePage(await apiFetch<ProductPage>("/products", { query: { ...query }, signal }));
}

export function getProduct(productId: string): Promise<WithEtag<ProductDetail>> {
  return fetchWithEtag<ProductDetail>(`/products/${productId}`);
}

export function createProduct(input: ProductCreateInput, idempotencyKey: string): Promise<Product> {
  return apiFetch<Product>("/products", { method: "POST", body: input, idempotencyKey });
}

export async function updateProduct(
  productId: string,
  input: ProductInput,
  ifMatch: string,
): Promise<WithEtag<ProductDetail>> {
  const { data, etag } = await apiRequest<ProductDetail>(`/products/${productId}`, {
    method: "PUT",
    body: input,
    ifMatch,
  });
  return { data, etag };
}

export async function listProductAttributes(
  productId: string,
  signal?: AbortSignal,
): Promise<ProductAttribute[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<ProductAttributePage>(`/products/${productId}/attributes`, {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function getProductAttribute(
  productId: string,
  attributeId: string,
): Promise<WithEtag<ProductAttribute>> {
  return fetchWithEtag<ProductAttribute>(`/products/${productId}/attributes/${attributeId}`);
}

export async function assignProductAttribute(
  productId: string,
  input: ProductAttributeInput,
  idempotencyKey: string,
): Promise<WithEtag<ProductAttribute>> {
  const { data, etag } = await apiRequest<ProductAttribute>(`/products/${productId}/attributes`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
  return { data, etag };
}

export async function updateProductAttribute(
  productId: string,
  attributeId: string,
  patch: ProductAttributePatch,
  ifMatch: string,
): Promise<WithEtag<ProductAttribute>> {
  const { data, etag } = await apiRequest<ProductAttribute>(
    `/products/${productId}/attributes/${attributeId}`,
    {
      method: "PATCH",
      body: patch,
      ifMatch,
      headers: { "Content-Type": "application/merge-patch+json" },
    },
  );
  return { data, etag };
}

export function unassignProductAttribute(productId: string, attributeId: string): Promise<void> {
  return apiFetch<void>(`/products/${productId}/attributes/${attributeId}`, {
    method: "DELETE",
  });
}

export async function listVariants(
  query: VariantListQuery,
  signal?: AbortSignal,
): Promise<Page<Variant>> {
  return toMutablePage(await apiFetch<VariantPage>("/variants", { query: { ...query }, signal }));
}

export async function lookupVariantsByBarcode(barcode: string): Promise<Variant[]> {
  return (await listVariants({ barcode, limit: 200 })).items;
}

export async function listAllVariants(q?: string, signal?: AbortSignal): Promise<Variant[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<VariantPage>("/variants", {
        query: {
          limit: 200,
          ...(q === undefined || q === "" ? {} : { q }),
          ...(cursor === undefined ? {} : { cursor }),
        },
        signal: pageSignal,
      }),
    { signal },
  );
}

export async function listVariantBarcodes(
  variantId: string,
  signal?: AbortSignal,
): Promise<VariantBarcode[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<BarcodePage>(`/variants/${variantId}/barcodes`, {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function addBarcode(
  variantId: string,
  input: BarcodeInput,
  idempotencyKey: string,
): Promise<VariantBarcode> {
  return apiFetch<VariantBarcode>(`/variants/${variantId}/barcodes`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export function removeBarcode(variantId: string, barcode: string): Promise<void> {
  return apiFetch<void>(`/variants/${variantId}/barcodes/${encodeURIComponent(barcode)}`, {
    method: "DELETE",
  });
}

export async function listProductComponents(
  productId: string,
  signal?: AbortSignal,
): Promise<ProductComponent[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<ProductComponentPage>(`/products/${productId}/components`, {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function addComponent(
  productId: string,
  input: ComponentInput,
  idempotencyKey: string,
): Promise<ProductComponent> {
  return apiFetch<ProductComponent>(`/products/${productId}/components`, {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export function removeComponent(productId: string, componentId: string): Promise<void> {
  return apiFetch<void>(`/products/${productId}/components/${componentId}`, {
    method: "DELETE",
  });
}

export async function listAllAttributes(signal?: AbortSignal): Promise<Attribute[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<AttributePage>("/attributes", {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function getAttribute(attributeId: string): Promise<WithEtag<Attribute>> {
  return fetchWithEtag<Attribute>(`/attributes/${attributeId}`);
}

export function createAttribute(
  input: AttributeCreateInput,
  idempotencyKey: string,
): Promise<Attribute> {
  return apiFetch<Attribute>("/attributes", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export async function updateAttribute(
  attributeId: string,
  input: AttributeInput,
  ifMatch: string,
): Promise<WithEtag<Attribute>> {
  const { data, etag } = await apiRequest<Attribute>(`/attributes/${attributeId}`, {
    method: "PUT",
    body: input,
    ifMatch,
  });
  return { data, etag };
}

export function deleteAttribute(attributeId: string): Promise<void> {
  return apiFetch<void>(`/attributes/${attributeId}`, { method: "DELETE" });
}

export async function listAllUoms(signal?: AbortSignal): Promise<Uom[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<UomPage>("/uoms", {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

/** Active UoMs are the only valid choices for newly-created references. */
export async function listActiveUoms(signal?: AbortSignal): Promise<Uom[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<UomPage>("/uoms", {
        query: { status: "active", limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function getUom(uomId: string): Promise<WithEtag<Uom>> {
  return fetchWithEtag<Uom>(`/uoms/${uomId}`);
}

export function createUom(input: UomCreateInput, idempotencyKey: string): Promise<Uom> {
  return apiFetch<Uom>("/uoms", { method: "POST", body: input, idempotencyKey });
}

export async function updateUom(
  uomId: string,
  patch: UomPatch,
  ifMatch: string,
): Promise<WithEtag<Uom>> {
  const { data, etag } = await apiRequest<Uom>(`/uoms/${uomId}`, {
    method: "PATCH",
    body: patch,
    ifMatch,
    headers: { "Content-Type": "application/merge-patch+json" },
  });
  return { data, etag };
}

export function deleteUom(uomId: string, ifMatch: string): Promise<void> {
  return apiFetch<void>(`/uoms/${uomId}`, { method: "DELETE", ifMatch });
}

export async function listAllUomConversions(signal?: AbortSignal): Promise<UomConversion[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<UomConversionPage>("/uom-conversions", {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export function createUomConversion(
  input: UomConversionInput,
  idempotencyKey: string,
): Promise<UomConversion> {
  return apiFetch<UomConversion>("/uom-conversions", {
    method: "POST",
    body: input,
    idempotencyKey,
  });
}

export function deleteUomConversion(fromUomId: string, toUomId: string): Promise<void> {
  return apiFetch<void>(`/uom-conversions/${fromUomId}/${toUomId}`, {
    method: "DELETE",
  });
}

export async function listAllTaxClasses(signal?: AbortSignal): Promise<TaxClass[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<TaxClassPage>("/tax-classes", {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export async function listAllCategories(signal?: AbortSignal): Promise<Category[]> {
  return collectCursorPages(
    (cursor, pageSignal) =>
      apiFetch<CategoryPage>("/categories", {
        query: { limit: 200, ...(cursor === undefined ? {} : { cursor }) },
        signal: pageSignal,
      }),
    { signal },
  );
}

export async function listProductImports(
  query: ImportListQuery = {},
  signal?: AbortSignal,
): Promise<Page<ProductImport>> {
  return toMutablePage(
    await apiFetch<ProductImportPage>("/product-imports", { query: { ...query }, signal }),
  );
}

export async function listProductImportRows(
  importId: string,
  query: ImportRowsQuery,
  signal?: AbortSignal,
): Promise<Page<ProductImportRow>> {
  return toMutablePage(
    await apiFetch<ProductImportRowPage>(`/product-imports/${importId}/rows`, {
      query: { ...query },
      signal,
    }),
  );
}

const multipartEncoder = new TextEncoder();

async function readImportBytes(file: Blob): Promise<ArrayBuffer> {
  if (file.size > MAX_IDEMPOTENCY_BLOB_BYTES) {
    throw new RangeError(`Catalog import exceeds ${MAX_IDEMPOTENCY_BLOB_BYTES} bytes`);
  }
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new TypeError("Catalog import reader returned a non-binary result"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Catalog import read failed"));
    reader.readAsArrayBuffer(file);
  });
}

function joinBytes(parts: readonly Uint8Array[]): ArrayBuffer {
  const total = parts.reduce((sum, part) => sum + part.byteLength, 0);
  const joined = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    joined.set(part, offset);
    offset += part.byteLength;
  }
  return joined.buffer;
}

async function materializeProductImportMultipart(
  file: Blob,
  allowExplicitSkuRenames: boolean,
  idempotencyKey: string,
): Promise<{ bytes: ArrayBuffer; contentType: string }> {
  const fileBytes = new Uint8Array(await readImportBytes(file));
  const boundarySeed = joinBytes([
    multipartEncoder.encode(`${idempotencyKey}\u0000${allowExplicitSkuRenames ? "1" : "0"}\u0000`),
    fileBytes,
  ]);
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) {
    throw new Error("Web Crypto SHA-256 is required for idempotent file uploads");
  }
  const digest = new Uint8Array(await subtle.digest("SHA-256", boundarySeed));
  const digestHex = Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("");
  // 64 ASCII characters: safely below RFC 2046's 70-character boundary cap.
  const boundary = `----erp-catalog-${digestHex.slice(0, 48)}`;
  const filePreamble = multipartEncoder.encode(
    `--${boundary}\r\n` +
      'Content-Disposition: form-data; name="file"; filename="catalog.csv"\r\n' +
      "Content-Type: text/csv\r\n\r\n",
  );
  const renamePart = allowExplicitSkuRenames
    ? multipartEncoder.encode(
        `\r\n--${boundary}\r\n` +
          'Content-Disposition: form-data; name="allow_explicit_sku_renames"\r\n\r\n' +
          "true",
      )
    : new Uint8Array();
  const closing = multipartEncoder.encode(`\r\n--${boundary}--\r\n`);
  return {
    bytes: joinBytes([filePreamble, fileBytes, renamePart, closing]),
    contentType: `multipart/form-data; boundary=${boundary}`,
  };
}

export async function uploadProductImport(
  file: Blob,
  allowExplicitSkuRenames: boolean,
  idempotencyKey: string,
): Promise<ProductImport> {
  // Native FormData is reserialized with a fresh boundary on every fetch.
  // The platform fingerprints exact raw bytes and normalized Content-Type, so
  // materialize this logical request once and replay those exact bytes.
  const serializedBody = await materializeProductImportMultipart(
    file,
    allowExplicitSkuRenames,
    idempotencyKey,
  );
  return apiFetch<ProductImport>("/product-imports", {
    method: "POST",
    serializedBody,
    idempotencyKey,
  });
}

export function getProductImport(importId: string): Promise<ProductImport> {
  return apiFetch<ProductImport>(`/product-imports/${importId}`);
}

export function commitProductImport(
  importId: string,
  idempotencyKey: string,
): Promise<ProductImport> {
  return apiFetch<ProductImport>(`/product-imports/${importId}/commit`, {
    method: "POST",
    idempotencyKey,
  });
}

export function discardProductImport(importId: string): Promise<void> {
  return apiFetch<void>(`/product-imports/${importId}`, { method: "DELETE" });
}

export interface ProductImportFailuresDownload {
  blob: Blob;
  filename: string;
}

const FAILURE_FILENAME_RE =
  /^catalog-import-[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}-failures\.csv$/;

/**
 * Download through a credentialed, branch-aware request. A plain `<a href>`
 * cannot carry `X-Acting-Branch-Id` and can therefore fetch the wrong branch's
 * staging report. The server filename is accepted only when it matches the
 * contract's closed safe pattern.
 */
export async function downloadProductImportFailures(
  importId: string,
): Promise<ProductImportFailuresDownload> {
  const headers = new Headers({ Accept: "text/csv, application/problem+json" });
  const branchId = getCurrentBranchId();
  if (branchId !== null) headers.set(ACTING_BRANCH_HEADER, branchId);

  const response = await fetch(
    `${API_BASE}/product-imports/${encodeURIComponent(importId)}/failures.csv`,
    { credentials: "include", headers },
  );
  if (!response.ok) {
    const problem = (await parseProblemResponse(response)) ?? undefined;
    if (response.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("erp:unauthorized"));
    }
    throw new ApiError(
      problem?.title ?? `Request failed with status ${response.status}`,
      response.status,
      problem,
      response.headers.get("x-request-id") ?? undefined,
    );
  }

  const disposition = response.headers.get("content-disposition") ?? "";
  const candidate = /^attachment;\s*filename="([^"]+)"$/.exec(disposition)?.[1];
  const fallback = `catalog-import-${importId}-failures.csv`;
  return {
    blob: await response.blob(),
    filename: candidate !== undefined && FAILURE_FILENAME_RE.test(candidate) ? candidate : fallback,
  };
}
